/**
 * Related products (storefront): the products a product links to by hand —
 * "Related products" and "Goes well with" — shown before the computed
 * recommendations (the `recommendations` slot).
 *
 * The server sends them as cards (`product.relatedProducts`,
 * `product.crossSellProducts`: { pid, name, thumbnail, amount, currency,
 * real_price, price_type, contentType, extensions } — aldoo-na related). The
 * framework's card is a plain one; a project shows its own cards by
 * registering a storefront extension of the same id that renders
 * `<RelatedProducts product renderRow={…} />` (aldoo.com:
 * components/Store/extensions/related/storefront.jsx).
 */
import React from "react"
import Typography from "aldoo-ra/Typography"
import { formatMoney } from "aldoo-ra/Store/currency"
import { useStoreExtensionText } from "../texts"

/** The two rows of a product: [{ id, title, products }], the empty ones left out. */
export function relatedRowsOf(product, text = (key) => key) {
  return [
    { id: "related", title: text("related_products"), products: product?.relatedProducts },
    { id: "cross-sell", title: text("cross_sell_products"), products: product?.crossSellProducts },
  ].filter((row) => Array.isArray(row.products) && row.products.length)
}

/** The framework's card: the picture, the name, the price; a link to /product/<pid>. */
export function RelatedCard({ product, href = `/product/${product.pid}` }) {
  const price = product.price_type !== "virtual" && Number.isFinite(product.amount) && product.currency ? formatMoney(product.amount, product.currency, { cents: true }) : ""
  return (
    <a href={href} className="flex flex-col w-[160px] shrink-0 gap-2 group" data-testid="related-card">
      <div className="aspect-square w-full rounded-lg overflow-hidden bg-neutral">
        {product.thumbnail && <img src={product.thumbnail} alt={product.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />}
      </div>
      <span className="text-sm font-medium line-clamp-2">{product.name}</span>
      {price && <span className="text-sm font-semibold">{price}</span>}
    </a>
  )
}

function DefaultRow({ title, products }) {
  return (
    <section className="w-full flex flex-col gap-4">
      <Typography component="h2" variant="h4" className="my-0">
        {title}
      </Typography>
      <div className="w-full overflow-x-auto no-scrollbar">
        <div className="flex flex-row flex-nowrap gap-5 w-max">
          {products.map((card) => (
            <RelatedCard key={card.pid} product={card} />
          ))}
        </div>
      </div>
    </section>
  )
}

/**
 * The rows; `renderRow({ id, title, products, pids })` draws one (the
 * framework's plain row by default). Nothing when the product links nowhere.
 */
export function RelatedProducts({ product, renderRow, className = "" }) {
  const text = useStoreExtensionText()
  const rows = relatedRowsOf(product, text)
  if (!rows.length) return null
  return (
    <div className={className || "w-full flex flex-col gap-10"} data-testid="related-products">
      {rows.map((row) => (
        <React.Fragment key={row.id}>
          {renderRow ? renderRow({ ...row, pids: row.products.map((card) => card.pid) }) : <DefaultRow {...row} />}
        </React.Fragment>
      ))}
    </div>
  )
}

export const StorefrontExtension = {
  id: "related",
  label: "Related products",
  slots: { recommendations: RelatedProducts },
}
